import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, CheckCircle, AlertCircle, Scale, Shield, CreditCard, Truck, Mail, HelpCircle, ShoppingBag, Phone } from 'lucide-react';

const Terms = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-pink-600 to-purple-700 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 mb-6">
              <FileText className="h-8 w-8" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms of Service</h1>
            <p className="text-xl text-pink-100">
              Terms and conditions governing your use of Sohkin Flowers services
            </p>
            <p className="mt-4 text-pink-200">Effective Date: December 1, 2023</p>
          </div>
        </div>
      </div>

      <div className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Agreement */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
              <h2 className="text-3xl font-bold mb-6">Agreement to Terms</h2>
              <p className="text-gray-600 mb-6">
                By accessing or using the Sohkin Flowers website, mobile application, or services, 
                you agree to be bound by these Terms of Service. If you disagree with any part of 
                the terms, you may not access our services.
              </p>
              
              <div className="bg-pink-50 p-6 rounded-xl mb-8">
                <div className="flex items-start">
                  <AlertCircle className="h-6 w-6 text-pink-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold mb-2">Important Notice</h4>
                    <p className="text-gray-700">
                      These terms affect your legal rights and obligations. Please read them carefully 
                      before using our services. We reserve the right to modify these terms at any time.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center">
                    <Scale className="h-6 w-6 text-pink-600 mr-3" />
                    Legal Agreement
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                      <span>You must be at least 18 years old to use our services</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                      <span>You agree to provide accurate and complete information</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                      <span>You are responsible for maintaining account security</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center">
                    <Shield className="h-6 w-6 text-pink-600 mr-3" />
                    Account Security
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                      <span>Keep your login credentials confidential</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                      <span>Notify us immediately of unauthorized access</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                      <span>We reserve the right to disable accounts if terms are violated</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Orders & Payments */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
              <h2 className="text-3xl font-bold mb-6">Orders & Payments</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center">
                    <ShoppingBag className="h-6 w-6 text-pink-600 mr-3" />
                    Order Acceptance
                  </h3>
                  <ul className="space-y-3 text-gray-600">
                    <li>• Order confirmation does not constitute acceptance</li>
                    <li>• We reserve the right to refuse or cancel any order</li>
                    <li>• Orders may be cancelled due to product unavailability</li>
                    <li>• Prices are subject to change without notice</li>
                    <li>• We are not responsible for typographical errors</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center">
                    <CreditCard className="h-6 w-6 text-pink-600 mr-3" />
                    Payment Terms
                  </h3>
                  <ul className="space-y-3 text-gray-600">
                    <li>• All payments must be made in Indian Rupees</li>
                    <li>• We accept multiple payment methods as listed</li>
                    <li>• Payment must be completed before order processing</li>
                    <li>• Refunds processed as per our refund policy</li>
                    <li>• Additional charges may apply for special requests</li>
                  </ul>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <h4 className="font-bold mb-4">Order Modifications & Cancellations</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-4 bg-white rounded-lg">
                    <div className="text-sm text-gray-600 mb-2">Within 1 Hour</div>
                    <div className="font-bold text-green-600">Free Modification/Cancellation</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg">
                    <div className="text-sm text-gray-600 mb-2">After 1 Hour</div>
                    <div className="font-bold text-yellow-600">Contact Support</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg">
                    <div className="text-sm text-gray-600 mb-2">After Processing</div>
                    <div className="font-bold text-red-600">May Not Be Possible</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Delivery Terms */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
              <h2 className="text-3xl font-bold mb-6 flex items-center">
                <Truck className="h-8 w-8 text-pink-600 mr-3" />
                Delivery Terms
              </h2>
              
              <div className="mb-8">
                <h3 className="text-2xl font-bold mb-4">Delivery Responsibility</h3>
                <div className="bg-blue-50 p-6 rounded-xl">
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <div className="w-2 h-2 rounded-full bg-blue-600 mt-2 mr-3 flex-shrink-0"></div>
                      <span>Delivery times are estimates, not guarantees</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 rounded-full bg-blue-600 mt-2 mr-3 flex-shrink-0"></div>
                      <span>We are not liable for delays beyond our control</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 rounded-full bg-blue-600 mt-2 mr-3 flex-shrink-0"></div>
                      <span>Risk of loss passes to you upon delivery</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 rounded-full bg-blue-600 mt-2 mr-3 flex-shrink-0"></div>
                      <span>Signature may be required for delivery</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold mb-4">Recipient Information</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li>• You are responsible for providing accurate delivery information</li>
                    <li>• Incorrect addresses may result in additional charges</li>
                    <li>• We are not liable for deliveries to incorrect addresses provided by you</li>
                    <li>• Additional charges apply for address corrections</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-4">Failed Delivery</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Two delivery attempts will be made</li>
                    <li>• Failed deliveries may incur additional charges</li>
                    <li>• Items may be returned to us after failed attempts</li>
                    <li>• Storage fees may apply for delayed pickups</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Intellectual Property */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
              <h2 className="text-3xl font-bold mb-6">Intellectual Property</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-bold mb-4">Our Content</h3>
                  <p className="text-gray-600 mb-4">
                    All content on our website, including text, graphics, logos, images, and software, 
                    is the property of Sohkin Flowers or its content suppliers and protected by copyright laws.
                  </p>
                  <div className="bg-pink-50 p-4 rounded-lg">
                    <p className="font-bold">You may not:</p>
                    <ul className="mt-2 space-y-1 text-sm">
                      <li>• Reproduce, distribute, or modify our content</li>
                      <li>• Use our trademarks without permission</li>
                      <li>• Reverse engineer our website or application</li>
                    </ul>
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-4">User Content</h3>
                  <p className="text-gray-600 mb-4">
                    By submitting reviews, photos, or other content, you grant us a non-exclusive, 
                    royalty-free license to use, modify, and display such content.
                  </p>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="font-bold">You represent that:</p>
                    <ul className="mt-2 space-y-1 text-sm">
                      <li>• You own or have rights to the content</li>
                      <li>• Content does not violate third-party rights</li>
                      <li>• Content is not offensive or inappropriate</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Limitation of Liability */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
              <h2 className="text-3xl font-bold mb-6">Limitation of Liability</h2>
              
              <div className="mb-8">
                <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                  <div className="flex items-start">
                    <AlertCircle className="h-6 w-6 text-red-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold mb-2">Important Limitation</h4>
                      <p className="text-gray-700">
                        To the maximum extent permitted by law, Sohkin Flowers shall not be liable for 
                        any indirect, incidental, special, consequential, or punitive damages resulting 
                        from your use of our services.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold mb-4">Maximum Liability</h3>
                  <p className="text-gray-600">
                    Our total liability for any claim arising from your use of our services shall not 
                    exceed the amount you paid for the specific product or service giving rise to the claim.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-4">Exceptions</h3>
                  <p className="text-gray-600">
                    These limitations do not apply to damages resulting from our gross negligence or 
                    willful misconduct, or where such limitations are prohibited by law.
                  </p>
                </div>
              </div>
            </div>

            {/* Governing Law */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
              <h2 className="text-3xl font-bold mb-6">Governing Law & Disputes</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-bold mb-4">Jurisdiction</h3>
                  <p className="text-gray-600 mb-4">
                    These Terms shall be governed by and construed in accordance with the laws of India, 
                    without regard to its conflict of law provisions.
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="font-bold">Dispute Resolution</p>
                    <p className="text-sm mt-2">
                      Any disputes shall be subject to the exclusive jurisdiction of the courts in Mumbai, 
                      Maharashtra.
                    </p>
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-4">Contact for Disputes</h3>
                  <div className="bg-pink-50 p-6 rounded-xl">
                    <h4 className="font-bold mb-2">Legal Department</h4>
                    <p className="text-gray-700 mb-4">
                      Sohkin Flowers Pvt. Ltd.<br />
                      123 Flower Street, Mumbai - 400001
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <Mail className="h-4 w-4 text-pink-600 mr-2" />
                        <span>legal@sohkinflowers.com</span>
                      </div>
                      <div className="flex items-center">
                        <Phone className="h-4 w-4 text-pink-600 mr-2" />
                        <span>1800-123-4567 (Legal Department)</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Section */}
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-6">Questions About Our Terms?</h2>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                If you have any questions about these Terms of Service, please contact us
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="mailto:legal@sohkinflowers.com"
                  className="inline-flex items-center justify-center bg-pink-600 text-white px-8 py-4 rounded-full font-bold hover:bg-pink-700 transition-colors"
                >
                  <Mail className="mr-2 h-5 w-5" />
                  Email Legal Department
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center border-2 border-pink-600 text-pink-600 px-8 py-4 rounded-full font-bold hover:bg-pink-50 transition-colors"
                >
                  <HelpCircle className="mr-2 h-5 w-5" />
                  Visit Contact Page
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;